/**
 * SERVE NO PAPEL?
 *
 * Nem todo item do banco sobrevive à impressora, e o nível de interação NÃO responde
 * essa pergunta sozinho — o caso da régua sobre o mapa mostra por quê. Ele é N2, o
 * mesmo nível de itens que imprimem sem perda nenhuma; mas medir a figura impressa só
 * funciona se a escala do papel for exatamente a do documento, se a impressora não
 * ajustar a página, e se o aluno tiver régua na mão. São três condições fora do
 * controle de quem monta a prova, e por isso ele não deve entrar numa prova impressa.
 *
 * O veredito sai da INSTÂNCIA, e não de uma etiqueta escrita à mão em cada item: assim
 * um item novo é classificado sem ninguém precisar lembrar de marcá-lo, e uma mudança
 * de estímulo muda o veredito junto. As regras:
 *
 *  - estímulo `measurable` — **não serve**: depende da escala impressa e de um
 *    instrumento físico;
 *  - estímulo que se mexe **e não é a resposta** — **serve com perda**: o papel recebe
 *    um quadro parado, escolhido pelo item (`quadroImpresso`, `staticFallbackAt`), e o
 *    aluno perde a exploração. Quando o controle É a resposta — a gangorra, a urna —,
 *    não há perda nenhuma: o quadro parado é o equivalente certo, e o aluno escreve o
 *    número em vez de arrastar;
 *  - construção com ferramenta de traçado (N4) — **serve, com o que se espera**: é a
 *    tarefa clássica de papel, desde que haja régua e compasso na mesa;
 *  - o resto — **serve**.
 *
 * O cálculo é preguiçoso e fica guardado: gerar as 42 instâncias de uma vez custaria
 * meio segundo na abertura da tela de montar, e quase ninguém usa o filtro.
 */

import { generate } from '../engine/generate.ts';
import { getTemplate } from '../engine/registry.ts';
import { seedSequence } from '../core/rng.ts';
import type { ItemInstance } from '../schema/item.ts';
import { FERRAMENTAS_DE_CONTROLE } from '../schema/item.ts';

export interface VeredictoPapel {
  /** Entra numa prova impressa sem enganar o professor? */
  readonly serve: boolean;
  /** Por que não serve — só quando não serve. */
  readonly motivo: string | null;
  /** O que se perde, ou o que é preciso ter na mesa — só quando serve. */
  readonly ressalva: string | null;
}

const SERVE: VeredictoPapel = { serve: true, motivo: null, ressalva: null };

export function papelDaInstancia(inst: ItemInstance): VeredictoPapel {
  if (inst.stimuli.some((s) => s.kind === 'measurable')) {
    return {
      serve: false,
      motivo:
        'mede a figura com régua: só funciona se a página for impressa exatamente na escala do documento, e se o aluno tiver régua na mão',
      ressalva: null,
    };
  }

  if (
    inst.interaction.kind === 'construct' &&
    inst.interaction.toolbelt.some((t) => !FERRAMENTAS_DE_CONTROLE.has(t))
  ) {
    return { serve: true, motivo: null, ressalva: 'o aluno precisa de régua e compasso na mesa' };
  }

  /*
   * A figura que se mexe é a RESPOSTA, ou é um instrumento de pensamento ao lado dela?
   * A distinção está em a interação apontar, ou não, para o estímulo que se mexe.
   *
   *  - a gangorra e a urna respondem PELO controle: o papel mostra o quadro parado e
   *    pede o número, que é o equivalente exato — nada se perde;
   *  - o ladrilhamento e o eixo truncado trazem um explorador AO LADO da pergunta: no
   *    papel ele vira uma figura só, e a exploração acaba.
   */
  const i = inst.interaction;
  const daResposta =
    i.kind === 'control-fit'
      ? i.stimulusId
      : i.kind === 'numeric-entry'
        ? (i.viaControl?.stimulusId ?? null)
        : null;
  const exploradorPerdido = inst.stimuli.some((s) => s.motion !== 'none' && s.id !== daResposta);
  if (exploradorPerdido) {
    return {
      serve: true,
      motivo: null,
      ressalva: 'a figura vai parada, e o aluno perde a exploração',
    };
  }

  return SERVE;
}

const cache = new Map<string, VeredictoPapel>();

/** O veredito de um item-modelo, por uma variante qualquer dele. */
export function papelDe(templateId: string): VeredictoPapel {
  const guardado = cache.get(templateId);
  if (guardado) return guardado;

  let v = SERVE;
  try {
    const t = getTemplate(templateId);
    for (const s of seedSequence(`papel-${templateId}`, 0, 12)) {
      const r = generate(t, s);
      if (r.status === 'ok') {
        v = papelDaInstancia(r.instance);
        break;
      }
    }
  } catch {
    // Item desconhecido: na dúvida, não atrapalha quem está montando.
  }
  cache.set(templateId, v);
  return v;
}

/** Quantos itens do percurso não devem ser impressos, e quais. */
export function naoServemNoPapel(templateIds: readonly string[]): string[] {
  return [...new Set(templateIds)].filter((id) => !papelDe(id).serve);
}
